import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { ContextMenuComponent } from '@syncfusion/ej2-react-navigations';

/**
 * @typedef {import('../../OpSchedulerNext.types').ContextMenuContext} ContextMenuContext
 * @typedef {import('../../OpSchedulerNext.types').ContextMenuItem} ContextMenuItem
 */

/**
 * Default scheduler context menu.
 *
 * Renders the built-in actions for the clicked event/cell followed by any
 * custom items provided through the scheduler's `contextMenu.items` prop.
 * Selecting an item automatically closes the menu.
 *
 * @param {Object} props
 * @param {ContextMenuContext} props.context
 * @param {ContextMenuItem[] | ((context: ContextMenuContext) => ContextMenuItem[])} [props.items]
 */
const OpSchedulerContextMenu = React.forwardRef(
  (
    {
      context,
      items,
      onClose,
      onDelete,
      onEdit,
      canUpdate = true,
      canDelete = true,
      xPosition,
      yPosition,
    },
    ref,
  ) => {
    const menuInstance = useRef(null);

    const defaultItems = [
      canUpdate && { key: 'edit', label: 'Edit', onClick: (ctx) => onEdit(ctx.event) },
      canDelete && { key: 'delete', label: 'Delete', onClick: (ctx) => onDelete(ctx.event) },
    ].filter(Boolean);

    const customItems = typeof items === 'function' ? items(context) : items || [];

    const menuItems = customItems.length > 0 ? customItems : defaultItems;

    // useCustomContextMenu detects outside clicks via `ref.current.contains`,
    // so expose the underlying menu DOM element behind that same shape.
    useImperativeHandle(
      ref,
      () => ({
        contains: (target) => menuInstance.current?.element?.contains(target) ?? false,
      }),
      [],
    );

    useEffect(() => {
      menuInstance.current?.open(yPosition, xPosition);
    }, [xPosition, yPosition]);

    const syncfusionItems = menuItems.map(({ key, label, disabled, iconCss }) => ({
      id: key,
      text: label,
      disabled,
      iconCss,
    }));

    const handleSelect = (args) => {
      const selected = menuItems.find((item) => item.key === args.item.id);
      try {
        selected?.onClick(context);
      } finally {
        onClose();
      }
    };

    return (
      <ContextMenuComponent
        ref={menuInstance}
        items={syncfusionItems}
        select={handleSelect}
        onClose={onClose}
      />
    );
  },
);

OpSchedulerContextMenu.displayName = 'OpSchedulerContextMenu';

export default OpSchedulerContextMenu;
